import { db } from '../cache/db'
import { gunzipJson } from '../cache/gzip'
import { warmImageCache } from './warmImages'

const RAN_AT_KEY = 'bskyhq:imageSalvageAt'
/** fetched_at high-water mark of rows already swept. After the first full
 *  walk, each daily run only inflates rows cached since the previous run
 *  instead of gunzipping the entire posts table on the main thread. */
const CURSOR_KEY = 'bskyhq:imageSalvageCursor'
const EVERY_MS = 24 * 60 * 60 * 1000
const CHUNK = 500

interface SalvagePost {
  embed?: unknown
  author?: { did?: string }
}

/**
 * Walk EVERY post in db.posts and warm its image URLs through the
 * service worker. Combined with the SW's force-cache fetches, this digs
 * previously-viewed images out of the browser's HTTP disk cache (even
 * stale entries for posts deleted upstream) and pins them in Cache
 * Storage before the browser evicts them. Throttled to once a day —
 * already-pinned URLs cost one cache.match each.
 */
export async function salvageCachedImages(): Promise<void> {
  try {
    if (!navigator.serviceWorker?.controller) return
    // Quota guard: this sweep force-caches image bytes, which is exactly what
    // fills the origin's storage. If we're already near the quota, skip it —
    // pinning more would push IndexedDB writes (logging, sync) into failure.
    try {
      const est = await navigator.storage?.estimate?.()
      if (est?.quota && est.usage != null && est.usage / est.quota > 0.8) return
    } catch { /* estimate unsupported — proceed */ }
    const last = Number(localStorage.getItem(RAN_AT_KEY) ?? 0)
    if (Date.now() - last < EVERY_MS) return
    localStorage.setItem(RAN_AT_KEY, String(Date.now()))

    const cursor = Number(localStorage.getItem(CURSOR_KEY) ?? 0)
    let maxSeen = cursor
    let offset = 0
    for (;;) {
      // fetched_at index: only rows cached since the last completed sweep.
      const rows = await db.posts
        .where('fetched_at')
        .above(cursor)
        .offset(offset)
        .limit(CHUNK)
        .toArray()
      if (rows.length === 0) break
      offset += rows.length
      const posts: SalvagePost[] = []
      for (const r of rows) {
        if (r.fetched_at > maxSeen) maxSeen = r.fetched_at
        try {
          const v = r.z
            ? await gunzipJson<SalvagePost>(r.z)
            : (r.value as unknown as SalvagePost)
          if (v?.embed) posts.push(v)
        } catch {
          /* skip unreadable rows */
        }
      }
      warmImageCache(posts)
      // Pace the sweep so a big cache doesn't flood the SW at startup.
      await new Promise((res) => setTimeout(res, 250))
    }
    // Advance only after a completed walk so an interrupted sweep retries.
    if (maxSeen > cursor) localStorage.setItem(CURSOR_KEY, String(maxSeen))
  } catch {
    // salvage is best-effort — never let it surface
  }
}
